import * as React from "react";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import { IBannerContent } from "@Interfaces/Api/cms/Banner";
import Link from "next/link";
import useMediaQuery from "@mui/material/useMediaQuery";
import Image from "next/image";
import { carouselHeroSkeleton } from "@Components/Loader/Skeleton";
import { useWindowSize } from "hooks/windowSize";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";

interface IBannerProps {
  homePage: boolean;
  allBanners: IBannerContent[];
}

function SwipeableTextMobileStepper({ homePage, allBanners }: IBannerProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [windowWidth] = useWindowSize();
  const safeWindowWidth = windowWidth || 360;
  const mobileHeight = Math.round(safeWindowWidth / 0.8);
  const desktopHeight = Math.round(safeWindowWidth / 2.8);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    if (allBanners && allBanners.length > 0) {
      setIsLoading(false);
    }
  }, [allBanners]);

  if (isLoading) {
    return <>{carouselHeroSkeleton}</>;
  }

  return (
    <Box
      sx={{
        maxWidth: "100%",
        flexGrow: 1,
        position: "relative",
        marginTop: { sm: "-2rem", xs: "" },
      }}
    >
      <Swiper
        modules={[Autoplay, Pagination]}
        spaceBetween={0}
        slidesPerView={1}
        autoplay={{ delay: 10000, disableOnInteraction: false }}
        pagination={{ clickable: true }}
        style={{
          width: "100%",
          height: isMobile ? mobileHeight + 4 : desktopHeight + 4,
        }}
      >
        {allBanners.map((step) => {
          const isVideo =
            step.media?.mobile?.url.includes(".mp4") ||
            step.media?.desktop?.url.includes(".mp4") ||
            step.media?.mobile?.url.includes(".webm") ||
            step.media?.desktop?.url.includes(".webm");

          const mediaUrl = isMobile
            ? step.media?.mobile?.url
            : step.media?.desktop?.url;

          const mediaType = mediaUrl?.includes(".webm")
            ? "video/webm"
            : "video/mp4";

          return (
            <SwiperSlide key={step._id}>
              {isVideo ? (
                homePage ? (
                  <Link href={step?.url!}>
                    <video
                      autoPlay
                      muted
                      loop
                      playsInline
                      style={{ width: "100%", height: "100%" }}
                    >
                      <source src={mediaUrl} type={mediaType} />
                    </video>
                  </Link>
                ) : (
                  <video
                    autoPlay
                    muted
                    loop
                    playsInline
                    style={{ width: "100%", height: "100%" }}
                  >
                    <source src={mediaUrl} type={mediaType} />
                  </video>
                )
              ) : homePage ? (
                <Link href={step?.url!}>
                  <Image
                    alt="banner-img"
                    src={mediaUrl!}
                    width={windowWidth}
                    height={isMobile ? mobileHeight : desktopHeight}
                    style={{ cursor: "pointer" }}
                    loading="eager"
                    priority
                  />
                </Link>
              ) : (
                <Image
                  alt="banner-img"
                  src={mediaUrl!}
                  width={windowWidth}
                  height={isMobile ? mobileHeight : desktopHeight}
                  style={{ cursor: "pointer" }}
                />
              )}
            </SwiperSlide>
          );
        })}
      </Swiper>
    </Box>
  );
}

export default SwipeableTextMobileStepper;
